import React, { useEffect, useMemo, useState } from 'react';
import { MessageBarBody, MessageBarTitle, Text } from '@fluentui/react-components';
import { ArrowDownload20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ServiceFilterCriteria } from '../ServiceList/ServiceList.Menu';
import ProtocolSelectorComponent from '../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';
import DateRangeSelectorComponent from '../../../commons/component/Inputs/DateRangeSelector/DateRangeSelector.Component';
import { ExportServicesByProtocol } from '../../../services/Service.Services';
import {
  buildExportFileName,
  buildServiceExportFilter
} from './ServiceExportDrawer.Functions';
import { useServiceExportDrawerStyles } from './ServiceExportDrawer.Styles';
import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { AppState } from '../../../redux/reducers';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';

interface ServiceExportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filterCriteria: ServiceFilterCriteria;
  searchCriteria?: string;
}

const ServiceExportDrawer: React.FC<ServiceExportDrawerProps> = ({
  isOpen,
  onClose,
  filterCriteria,
  searchCriteria
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const styles = useServiceExportDrawerStyles();
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedProtocols, setSelectedProtocols] = useState<Protocol[] | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(filterCriteria.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(filterCriteria.endDate);

  const MedicalProtocolsState = useSelector((state: AppState) => state.MedicalProtocols);

  useEffect(() => {
    if (isOpen) {
      setStartDate(filterCriteria.startDate);
      setEndDate(filterCriteria.endDate);

      if (filterCriteria.protocolId && MedicalProtocolsState?.list?.length) {
        const selectedProtocol = MedicalProtocolsState.list.find((protocol: Protocol) => protocol.id === filterCriteria.protocolId);
        setSelectedProtocols(selectedProtocol ? [selectedProtocol] : undefined);
      } else {
        setSelectedProtocols(undefined);
      }
    }
  }, [isOpen, filterCriteria.endDate, filterCriteria.protocolId, filterCriteria.startDate, MedicalProtocolsState?.list]);

  useEffect(() => {
    if (isOpen && (!MedicalProtocolsState?.list || MedicalProtocolsState.list.length === 0)) {
      dispatch(MedicalProtocolStore.actionCreators.GetAllMedicalProtocols());
    }
  }, [MedicalProtocolsState?.list, dispatch, isOpen]);

  const selectedProtocolId = selectedProtocols && selectedProtocols.length > 0
    ? selectedProtocols[0].id
    : undefined;

  const summaryLines = useMemo(() => {
    const lines: string[] = [];

    const selectedProtocolLabel = selectedProtocols && selectedProtocols.length > 0
      ? selectedProtocols[0].title
      : undefined;
    if (selectedProtocolLabel) {
      lines.push(`${t('servicesListPage.filters.protocol')}: ${selectedProtocolLabel}`);
    }

    if (startDate || endDate) {
      lines.push(
        t('servicesListPage.export.summaryPeriod', {
          defaultValue: 'Periodo: {{start}} - {{end}}',
          start: startDate ? startDate.toLocaleDateString() : '-',
          end: endDate ? endDate.toLocaleDateString() : '-'
        })
      );
    }

    if (searchCriteria) {
      lines.push(
        t('servicesListPage.export.summarySearch', {
          defaultValue: 'Busqueda: {{search}}',
          search: searchCriteria
        })
      );
    }

    if (!lines.length) {
      lines.push(
        t('servicesListPage.export.summaryAll', {
          defaultValue: 'Se exportaran todos los servicios del protocolo seleccionado.'
        })
      );
    }

    return lines;
  }, [startDate, endDate, searchCriteria, selectedProtocols, t]);

  const handleExport = async () => {
    if (selectedProtocolId === undefined || selectedProtocolId === null) {
      setErrorMessage(
        t('servicesListPage.export.protocolRequired', {
          defaultValue: 'Debe seleccionar un protocolo para exportar servicios.'
        })
      );
      return;
    }

    try {
      setErrorMessage(null);
      setIsExporting(true);

      const localFilterCriteria: ServiceFilterCriteria = {
        protocolId: selectedProtocolId,
        startDate,
        endDate
      };

      const filter = buildServiceExportFilter(localFilterCriteria, searchCriteria);
      const blob = await ExportServicesByProtocol(selectedProtocolId, filter);

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildExportFileName();
      document.body.appendChild(anchor);
      anchor.click();
      URL.revokeObjectURL(url);
      anchor.remove();

      onClose();
    } catch {
      setErrorMessage(
        t('servicesListPage.export.error', {
          defaultValue: 'No se pudo exportar el archivo. Intente nuevamente.'
        })
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GuideDrawer
      open={isOpen}
      size="small"
      title={t('servicesListPage.export.title', { defaultValue: 'Exportar servicios' })}
      onDismiss={onClose}
      closeLabel={t('servicesListPage.export.close', { defaultValue: 'Cerrar' })}
      testId="service-export-drawer"
      secondaryAction={{ label: t('servicesListPage.export.cancel', { defaultValue: 'Cancelar' }) }}
      primaryAction={{
        label: t('servicesListPage.export.download', { defaultValue: 'Descargar Excel' }),
        onClick: handleExport,
        loading: isExporting,
        icon: <ArrowDownload20Regular />
      }}
    >
      {errorMessage && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>
              {t('servicesListPage.export.errorTitle', {
                defaultValue: 'Error de exportacion'
              })}
            </MessageBarTitle>
            {errorMessage}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideHelpText>
        {t('servicesListPage.export.description', {
          defaultValue:
            'Descargue un Excel por periodo y protocolo con los filtros actuales.'
        })}
      </GuideHelpText>

      <ProtocolSelectorComponent
        selectionLimit={1}
        selectedProtocols={selectedProtocols}
        onChangeSelection={setSelectedProtocols}
      />

      <DateRangeSelectorComponent
        startDate={startDate}
        endDate={endDate}
        onDateChange={(nextStartDate, nextEndDate) => {
          setStartDate(nextStartDate);
          setEndDate(nextEndDate);
        }}
        startDateLabel={t('servicesListPage.filters.startDate')}
        endDateLabel={t('servicesListPage.filters.endDate')}
      />

      <div className={styles.summaryBox} data-testid="service-export-summary">
        {summaryLines.map((line) => (
          <Text key={line} className={styles.summaryLine}>
            {line}
          </Text>
        ))}
      </div>
    </GuideDrawer>
  );
};

export default ServiceExportDrawer;
